<script setup lang="ts">
// Versandkostenfrei-Fortschrittsbalken für den Warenkorb.
// Rot, solange die Schwelle nicht erreicht ist – Grün, sobald genug im Warenkorb liegt.
// Die Schwelle wird aus der Store API (Versandart-Preismatrix) geholt; bis zum Laden
// bzw. bei Fehler greift der Fallback.
const FALLBACK_THRESHOLD = 250; // in Shop-Währung (CHF)

const { subtotal } = useCart();
const { currencyCode } = usePrice();
const { threshold, loadFreeShippingThreshold } = useFreeShippingThreshold();

onMounted(loadFreeShippingThreshold);

// Effektive Schwelle: API-Wert, sonst Fallback. Abgerundet auf die ganze Einheit,
// da die Store API den Gratis-Tier z. B. bei 250.01 beginnen lässt (Bereichsartefakt:
// bezahlt bis 250, gratis ab 250.01) – ohne Abrunden würden die Restbeträge auf .01 enden.
const freeShippingThreshold = computed(() =>
  Math.floor(threshold.value ?? FALLBACK_THRESHOLD),
);

// Aktueller Warenwert (Zwischensumme der Positionen), leicht entprellt:
// Bei schnellen Mengenänderungen kommen mehrere Cart-Updates kurz nacheinander –
// die Entprellung lässt den Balken nur einmal auf den Endwert animieren statt
// mehrfach "nachzujittern".
const rawCurrent = computed(() => subtotal.value || 0);
const current = refDebounced(rawCurrent, 300);

// Schwelle erreicht?
const reached = computed(() => current.value >= freeShippingThreshold.value);

// Wie viel fehlt noch bis zum Gratisversand?
const remaining = computed(() =>
  Math.max(0, freeShippingThreshold.value - current.value),
);

// Füllstand des Balkens in Prozent (0–100)
const percentage = computed(() => {
  const t = freeShippingThreshold.value;
  if (t <= 0) return 100;
  return Math.min(100, Math.round((current.value / t) * 100));
});

// Fehlbetrag formatiert, z. B. "CHF 45.00" (Währung vorne, wie im restlichen Shop)
const remainingFormatted = computed(() => {
  const amount = new Intl.NumberFormat("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(remaining.value);
  return `${currencyCode.value} ${amount}`;
});
</script>

<template>
  <div class="my-6" data-testid="free-shipping-bar">
    <!-- Balken / Track -->
    <div
      class="h-3 w-full overflow-hidden rounded-full bg-gray-100"
      role="progressbar"
      :aria-valuenow="percentage"
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <!-- Füllung -->
      <div
        class="h-full rounded-full transition-all duration-500 ease-out"
        :class="reached ? 'bg-skin-theme' : 'bg-[#B42318]'"
        :style="{ width: percentage + '%' }"
      />
    </div>

    <!-- Hinweistext -->
    <p
      class="mt-3 text-xl font-bold leading-snug"
      :class="reached ? 'text-skin-theme' : 'text-[#B42318]'"
      data-testid="free-shipping-text"
    >
      <template v-if="reached">
        <span class="fa-solid fa-check mr-2" aria-hidden="true" />
        {{ $t("cart.free_shipping.reached") }}
      </template>
      <template v-else>
        {{ $t("cart.free_shipping.remaining", { amount: remainingFormatted }) }}
      </template>
    </p>
  </div>
</template>
